import React, { useRef, useState, useEffect } from 'react';
import './Hero.css';

// Carousel images
const CAROUSEL_IMAGES = [
  '/images/heroimage1.png?v=2',
  '/images/heroimage2.png?v=2',
  '/images/heroimage3.png?v=2',
  '/images/heroimage4.png?v=2',
  '/images/heroimage5.png?v=2',
  '/images/heroimage6.png?v=2'
];

const Hero = () => {
  const heroContainerRef = useRef(null);
  const heroImageRef = useRef(null);
  const [tiltStyle, setTiltStyle] = useState({});
  const [imageTiltStyle, setImageTiltStyle] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-rotate carousel every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % CAROUSEL_IMAGES.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Preload first image for better LCP
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = CAROUSEL_IMAGES[0];
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const requestRef = useRef();

  const handleMouseMove = (e) => {
    if (!heroContainerRef.current || !heroImageRef.current) return;

    if (requestRef.current) return;

    requestRef.current = requestAnimationFrame(() => {
      const container = heroContainerRef.current;
      const image = heroImageRef.current;
      const containerRect = container.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();

      const containerX = e.clientX - containerRect.left;
      const containerY = e.clientY - containerRect.top;
      const containerCenterX = containerRect.width / 2;
      const containerCenterY = containerRect.height / 2;

      const imageX = e.clientX - imageRect.left;
      const imageY = e.clientY - imageRect.top;
      const imageCenterX = imageRect.width / 2;
      const imageCenterY = imageRect.height / 2;

      const containerRotateX = ((containerY - containerCenterY) / containerCenterY) * -2;
      const containerRotateY = ((containerX - containerCenterX) / containerCenterX) * 2;

      const imageRotateX = ((imageY - imageCenterY) / imageCenterY) * -4;
      const imageRotateY = ((imageX - imageCenterX) / imageCenterX) * 4;

      setTiltStyle({
        transform: `perspective(1000px) rotateX(${containerRotateX}deg) rotateY(${containerRotateY}deg)`,
      });

      setImageTiltStyle({
        transform: `perspective(1000px) rotateX(${imageRotateX}deg) rotateY(${imageRotateY}deg) scale(1.02)`,
      });

      requestRef.current = null;
    });
  };

  useEffect(() => {
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  const handleMouseLeave = () => {
    setTiltStyle({
      transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg)',
    });
    setImageTiltStyle({
      transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
    });
  };

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const heroBgStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url('/images/hero-bg.jpg.png')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    opacity: 0.1,
    zIndex: 0,
    pointerEvents: 'none'
  };

  return (
    <section id="home" className="hero">
      <div style={heroBgStyle}></div>
      <div 
        className="hero-container"
        ref={heroContainerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className="hero-content" style={tiltStyle}>
          <div className="hero-ribbon-wrapper">
            <div className="ribbon-tail-left"></div>
            <div className="ribbon-content">
              Shuddhata se Samjhota Nahi
            </div>
            <div className="ribbon-tail-right"></div>
          </div>
          <h1 className="hero-title">
            <span className="title-part1">From the Land of Ram Rajya,</span>
            <span className="title-part2">A Way of Living Continues</span>
          </h1>
          <p className="hero-description">
            We carry those values forward in <span className="highlight">our dairy</span>, <span className="highlight">our food</span>, and <span className="highlight">everything</span> that enters your home
          </p>
          <div className="hero-buttons">
            <a 
              href="#products" 
              className="btn btn-primary"
              onClick={(e) => scrollToSection(e, 'products')}
            >
              Explore Products
            </a>
            <a 
              href="#story" 
              className="btn btn-secondary"
              onClick={(e) => scrollToSection(e, 'story')}
            >
              Story
            </a>
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <svg className="stat-icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-text">
                <div className="stat-value">100% Pure</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <svg className="stat-icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21V19C22.9993 18.1137 22.7044 17.2522 22.1614 16.5523C21.6184 15.8524 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11768 19.0078 7.005C19.0078 7.89232 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-text">
                <div className="stat-value">50K+ Families</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <svg className="stat-icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-text">
                <div className="stat-value">A+ Quality</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-image" ref={heroImageRef}>
          <div className="hero-illustration" style={imageTiltStyle}>
            <div className="illustration-frame">
              <div className="illustration-content">
                <div className="carousel-container">
                  {CAROUSEL_IMAGES.map((image, index) => {
                    const offset = index - currentImageIndex;
                    let className = 'hero-photo carousel-image';
                    
                    // Normalize offset for circular carousel
                    let normalizedOffset = offset;
                    if (normalizedOffset > CAROUSEL_IMAGES.length / 2) {
                      normalizedOffset -= CAROUSEL_IMAGES.length;
                    } else if (normalizedOffset < -CAROUSEL_IMAGES.length / 2) {
                      normalizedOffset += CAROUSEL_IMAGES.length;
                    }
                    
                    if (normalizedOffset === 0) {
                      className += ' active';
                    } else if (normalizedOffset === -1) {
                      className += ' prev';
                    } else if (normalizedOffset === 1) {
                      className += ' next';
                    } else {
                      // Hide images that are not immediately adjacent
                      className += ' hidden';
                    }

                    return (
                      <img
                        key={image}
                        src={image}
                        alt={`Ayodhya Dairy Banner ${index + 1}`}
                        className={className}
                        loading={index === 0 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

